/**
 * Number Formatting Utility
 * Formats numbers based on company number_format setting
 */

/**
 * Format a number according to company number format setting
 *
 * @param value - The numeric value to format
 * @param format - Company number format (e.g., "1,234.56", "1.234,56", "1 234.56")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string
 *
 * Examples:
 * - formatNumber(1234.56, "1,234.56") → "1,234.56"
 * - formatNumber(1234.56, "1.234,56") → "1.234,56"
 * - formatNumber(1234.56, "1 234.56") → "1 234.56"
 */
export function formatNumber(
  value: number | string | null | undefined,
  format: string = "1,234.56",
  decimals: number = 2
): string {
  // Handle null/undefined/empty
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if valid number
  if (isNaN(numValue)) {
    return String(value);
  }

  // Determine format type based on the format string
  let formatted: string;

  if (format.includes(',') && format.endsWith('.56')) {
    // Format: "1,234.56" (US/UK style - comma as thousand separator, dot as decimal)
    formatted = numValue.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } else if (format.includes('.') && format.endsWith(',56')) {
    // Format: "1.234,56" (European style - dot as thousand separator, comma as decimal)
    formatted = numValue.toLocaleString('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } else if (format.includes(' ')) {
    // Format: "1 234.56" (Space as thousand separator, dot as decimal)
    // Custom formatting to ensure proper space grouping
    const parts = numValue.toFixed(decimals).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const decimalPart = parts[1];
    formatted = integerPart + '.' + decimalPart;
  } else {
    // Fallback to comma format
    formatted = numValue.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  return formatted;
}

/**
 * Format currency amount with company settings
 *
 * @param value - The numeric value
 * @param currency - Currency code (e.g., 'SGD', 'USD')
 * @param format - Company number format
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currency: string = 'SGD',
  format: string = "1,234.56"
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const formatted = formatNumber(value, format, 2);
  return formatted ? `${currency} ${formatted}` : '';
}

/**
 * Parse formatted number string back to number
 * Handles all format types (comma, dot, space separators)
 *
 * @param formatted - Formatted string like "1,234.56" or "1.234,56"
 * @returns Number value
 */
export function parseFormattedNumber(formatted: string): number {
  if (!formatted || typeof formatted !== 'string') return 0;

  // Remove currency symbols and trim
  let cleaned = formatted.replace(/[A-Z]{3}\s*/g, '').trim();

  // Detect format type
  const lastDotIndex = cleaned.lastIndexOf('.');
  const lastCommaIndex = cleaned.lastIndexOf(',');

  if (lastCommaIndex > lastDotIndex) {
    // European format: "1.234,56" - comma is decimal separator
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDotIndex > lastCommaIndex) {
    // US format: "1,234.56" - dot is decimal separator
    cleaned = cleaned.replace(/,/g, '');
  } else {
    // Space separator: "1 234.56"
    cleaned = cleaned.replace(/\s/g, '');
  }

  // Remove any remaining non-numeric characters except dot and minus
  cleaned = cleaned.replace(/[^\d.-]/g, '');

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}
